import React, { MouseEvent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { query, repository, subscribe } from '@trlogic/client-js';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import Tracker from '../../domain/tracker/Tracker';
import { enterComponent, leaveComponent } from 'redux/actions/ComponentActions';
import { changeTracker, changeTrackerDirty, changeTrackerIsUpdate, clearTracker } from 'redux/actions/TrackerActions';
import { RootState } from '../../redux/store';
import { TrackerState } from '../../redux/reducers/TrackerReducer';
import { TransactionJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { clearPage } from 'redux/actions/PageActions';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import TrackerService from '../../service/TrackerService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import Injectables from '../../constant/Injectables';
import ComponentName from '../../constant/ComponentName';
import CustomLoading from '../custom/CustomLoading';
import Breadcrumb from '../Breadcrumb';
import CustomEditorItemComponent from '../custom/CustomEditorItemComponent';
import CustomTabv2 from '../custom/CustomTabv2';
import RoleHelper from '../../RoleHelper';
import CollectionNames from '../../constant/CollectionNames';
import ValueFactory from '../../util/ValueFactory';
import { isStringValid } from '../../util/Generator';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import ComponentAuditLog from '../ComponentAuditLog';
import { changeEventDirty } from '../../redux/actions/ChannelActions';
import TrackerMappingComponent from './detail/TrackerMappingComponent';
import TrackerEventComponent from './detail/TrackerEventComponent';
import TrackerVariableComponent from './detail/TrackerVariableComponent';
import TrackerAccessComponent from './detail/TrackerAccessComponent';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class TrackerEditorContainer extends StandardEditorContainerComponent<Props, State, Tracker> {
  public state: State = new State();

  private trackerSubscription: subscribe.Subscription | undefined;
  private transactionSubscription: subscribe.Subscription | undefined;
  private trackerComputation: subscribe.Computation | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly trackerService: TrackerService = ServiceContainer.getInstance().get(ServiceNames.TRACKER);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.TRACKER,
      icon: 'tracker',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      tracker: { tracker, isUpdate },
    } = this.props;
    const { loading } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={tracker.name}
          description={tracker.description}
          status={tracker?.stage || 'DRAFT'}
          component={ComponentName.TRACKER}
          isUpdate={isUpdate}
          icon={'tracker'}
          onChangeName={(name) => this.onChangeName(name)}
          onChangeDescription={(description) => this.onChangeDescription(description)}
          onSave={() => this.onSave()}
          onRemove={(e) => this.onRemove(e)}
          onDeploy={(e) => this.onDeploy(e)}
          onUndeploy={(e) => this.onUndeploy(e)}
          disableSave={!RoleHelper.canEdit('Tracker')}
          disableRemove={!RoleHelper.canEdit('Tracker')}
          disableNameChange={!RoleHelper.canEdit('Tracker')}
          disableDescriptionChange={!RoleHelper.canEdit('Tracker')}
          disableDeploy={!RoleHelper.canDeploy('Tracker')}
          disableUndeploy={!RoleHelper.canDeploy('Tracker')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full'}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearTracker } = this.props;
    clearTracker();
    await super.componentWillUnmount();
  }

  protected _startTracker = () => {
    const eventId = this.getItemIdFromRoute();
    if (eventId == undefined) {
      this.setState({ loading: false });
    }
    const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    const trackerQuery = query.QueryBuilder.select().from(CollectionNames.TRACKERS).where(eq('id', eventId)).build();
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery);
    this.trackerSubscription = subscribe.Formica.subscribe(CollectionNames.TRACKERS, trackerQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
    this.trackerComputation = subscribe.Tracker.autorun(this.trackerTracker, [this.trackerSubscription]);
  };

  protected _stopTracker = async () => {
    await this.trackerSubscription?.stop();
    await this.transactionSubscription?.stop();
    await this.trackerComputation?.stop();
    this.computation?.stop();
  };

  protected tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ transactions: transactions });
    }
  };

  private trackerTracker = async () => {
    const { changeTrackerDirty } = this.props;
    if (this.trackerSubscription?.ready()) {
      const eventId = this.getItemIdFromRoute();
      if (eventId == undefined) {
        this.changeIsUpdate(false);
        this.setState({ loading: false });
        RoleHelper.setCurrentAccess('Edit');
        changeTrackerDirty(false);
        return;
      }
      const trackerQuery = query.QueryBuilder.select().from(CollectionNames.TRACKERS).where(eq('id', eventId)).build();
      const tracker: Tracker = (await this.repository.find(trackerQuery)) || ValueFactory.initializeTracker();
      this.changeItem(tracker);
      this.changeIsUpdate(true);
      RoleHelper.setCurrentAccess(tracker);
      changeEventDirty(false);
      this.setState({ loading: false });
    }
  };

  private changeIsUpdate = (value: boolean) => {
    const { changeTrackerIsUpdate } = this.props;
    changeTrackerIsUpdate(value);
  };

  private switchToUpdate = async () => {
    const {
      tracker: { tracker },
      router: { navigate, params },
      enterComponent,
    } = this.props;
    if (tracker.name == '') return;
    enterComponent({ component: ComponentName.TRACKER, id: tracker.name });
    navigate(`/tracker${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${tracker.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  private onChangeName = (name: string) => {
    const {
      tracker: { tracker },
    } = this.props;
    const value = name.trim();
    if (isStringValid(value)) {
      tracker.name = value;
      this.changeItem(tracker);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      tracker: { tracker },
    } = this.props;
    tracker.description = description;
    this.changeItem(tracker);
  };

  private onSave = async () => {
    const {
      tracker: { tracker },
      isUpdate,
      lastCategory,
      changeTrackerDirty,
    } = this.props;
    try {
      const isSaved = await this.trackerService.saveTracker(tracker, isUpdate, lastCategory);
      if (isSaved) {
        changeTrackerDirty(false);
        this.changeIsUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private onRemove = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      tracker: { tracker },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.trackerService.removeTracker(tracker);
        if (success) {
          navigate(`/tracker`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeTracker', defaultMessage: 'Are you sure to remove this tracker ?' }),
    });
  };

  private onDeploy = (e: any) => {
    const {
      tracker: { tracker },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.trackerService.saveTracker(tracker, true, {} as EnrichedComponentCategory, true);
        await this.trackerService.deployTracker(tracker);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deployTracker', defaultMessage: 'Are you sure to deploy this tracker ?' }),
    });
  };

  private onUndeploy = async (e: any) => {
    const {
      tracker: { tracker },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.TRACKER, [tracker.name], () => this.trackerService.unDeployTracker(tracker)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeployTracker', defaultMessage: 'Are you sure to undeploy this tracker ?' }),
    });
  };

  protected getTabs = () => {
    const { transactions } = this.state;
    return [
      {
        key: 'Mapping',
        label: LanguageUtil.get({ id: 'common.tabs.mapping', defaultMessage: 'Mapping' }),
        content: this.transactionSubscription?.ready() ? <TrackerMappingComponent transactions={transactions} /> : null,
      },
      {
        key: 'Event',
        label: LanguageUtil.get({ id: 'common.tabs.event', defaultMessage: 'event' }),
        content: <TrackerEventComponent />,
      },
      {
        key: 'Variable',
        label: LanguageUtil.get({ id: 'common.tabs.variable', defaultMessage: 'variable' }),
        content: <TrackerVariableComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'access' }),
        content: <TrackerAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.TRACKER, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'audit' }),
        content: <ComponentAuditLog component={ComponentName.TRACKER} documentName={this.props.tracker.tracker.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.TRACKER, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: Tracker) => {
    const { changeTracker, changeTrackerDirty } = this.props;
    changeTracker(item);
    changeTrackerDirty(true);
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
  transactions: TransactionJsonModel[] = [];
}

const mapStateToProps = (state: RootState) => ({
  tracker: state.tracker as TrackerState,
  lastCategory: state.page.lastCategory,
  isUpdate: state.tracker.isUpdate,
});

const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeTracker,
  changeTrackerIsUpdate,
  clearTracker,
  clearPage,
  changeTrackerDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(TrackerEditorContainer));
